import { navigationEpoch } from '../navigation/loadEpoch';
import { getPersistenceNamespace } from './helpers/persistenceHelpers';
import { create } from 'zustand';
import {
  DraftModel,
  DraftModelSummary,
  DraftTable,
  DraftColumn,
  DraftRelationship,
  DraftMetric,
  DraftDimension,
  DraftEntity,
  DraftMetricGroup,
  DraftAggregateSource,
  DraftModelMetadata,
  ModelType,
  StudioChatMessage,
  StudioActions,
  ValidationResult,
  PublishPreview,
  DiscoveryReviewItem,
  DiscoveryConfirmText,
  DiscoveryCodes,
} from '../studioTypes';
import {
  getStudioDrafts,
  getStudioDraft,
  createStudioDraft,
  updateStudioDraft,
  deleteStudioDraft,
  validateStudioModel,
  publishStudioModel,
  previewStudioPublish,
  studioSuggestJoins,
  studioSuggestDisplayNames,
  studioClassifyColumns,
  loadDraftFromModel as apiLoadDraftFromModel,
  getProductionSnapshot,
  studioAgentChat,
  getDiscoveryReview,
  confirmDiscoveryItem as apiConfirmDiscoveryItem,
  confirmDiscoveryBatch as apiConfirmDiscoveryBatch,
} from '../apis';
import { generateId, getCurrentUser } from '../utils';

// ============================================================
// State interface
// ============================================================

export interface StudioState {
  // Draft
  draftModel: DraftModel | null;
  baselineModel: DraftModel | null;
  isDirty: boolean;
  draftsList: DraftModelSummary[];

  // Conflict detection
  productionConflict: boolean;

  // Source context (when navigated from Workspace).
  // breadcrumb holds prior hops when jumping between inherited entity drafts;
  // leftmost entry is the oldest hop. Empty array = entered directly.
  sourceContext: {
    domain: string;
    perspective: string;
    modelName: string;
    breadcrumb: Array<{ modelName: string; displayName: string }>;
  } | null;

  // Chat
  studioChatMessages: StudioChatMessage[];
  studioChatThreadId: string | null;
  isStudioThinking: boolean;
  studioChatProgress: Array<{ id: string; text: string; done: boolean }>;
  studioAgentPanelOpen: boolean;
  studioAgentPanelWidth: number;
  toggleStudioAgentPanel: () => void;
  setStudioAgentPanelWidth: (w: number) => void;

  // Publish preview
  publishPreview: PublishPreview | null;
  isPreviewLoading: boolean;

  // UI
  activePreviewTab: number;
  /** Stroma (#566): the Studio home tab being shown, or null while the editor is. A loaded draft stays in memory either way. */
  studioHome: StudioHomeTab | null;
  validationResult: ValidationResult | null;
  isLoading: boolean;
  error: string | null;

  // Actions — Draft CRUD
  createNewDraft: (name: string, metadata?: Partial<DraftModelMetadata>) => Promise<void>;
  loadDraft: (id: string, signal?: AbortSignal) => Promise<void>;
  loadDraftFromModel: (modelName: string, context?: { domain: string; perspective: string }, opts?: { preserveChat?: boolean }) => Promise<void>;
  saveDraft: () => Promise<void>;
  /** Resolves false when the list could not be read, or the workspace changed while it was read. */
  fetchDraftsList: () => Promise<boolean>;
  deleteDraft: (id: string) => Promise<boolean>;

  // Navigate into an inherited entity's draft, pushing the current draft onto
  // sourceContext.breadcrumb. Current draft is auto-saved if dirty.
  navigateToInheritedEntity: (targetModel: string) => Promise<void>;
  // Pop breadcrumb back to a specific index (0 = first/oldest hop). Current
  // draft is auto-saved before swapping back.
  popBreadcrumbTo: (index: number) => Promise<void>;

  // Actions — Model mutations
  addTable: (table: DraftTable) => void;
  updateTable: (name: string, updates: Partial<DraftTable>) => void;
  removeTable: (name: string) => void;
  addColumn: (tableName: string, column: DraftColumn) => void;
  updateColumn: (tableName: string, columnName: string, updates: Partial<DraftColumn>) => void;
  updateColumnByIndex: (tableName: string, index: number, updates: Partial<DraftColumn>) => void;
  removeColumn: (tableName: string, columnName: string) => void;
  removeColumnByIndex: (tableName: string, index: number) => void;
  addRelationship: (rel: DraftRelationship) => void;
  updateRelationship: (name: string, updates: Partial<DraftRelationship>) => void;
  removeRelationship: (name: string) => void;
  addMetric: (metric: DraftMetric) => void;
  updateMetric: (name: string, updates: Partial<DraftMetric>) => void;
  removeMetric: (name: string) => void;
  addDimension: (dim: DraftDimension) => void;
  updateDimension: (name: string, updates: Partial<DraftDimension>) => void;
  removeDimension: (name: string) => void;
  addEntity: (entity: DraftEntity) => void;
  updateEntity: (name: string, updates: Partial<DraftEntity>) => void;
  removeEntity: (name: string) => void;
  addMetricGroup: (group: DraftMetricGroup) => void;
  updateMetricGroup: (name: string, updates: Partial<DraftMetricGroup>) => void;
  removeMetricGroup: (name: string) => void;
  setMetricGroups: (groups: DraftMetricGroup[]) => void;
  addAggregateSource: (source: DraftAggregateSource) => void;
  updateAggregateSource: (table: string, updates: Partial<DraftAggregateSource>) => void;
  removeAggregateSource: (table: string) => void;
  setModelType: (type: ModelType) => void;
  setCoreModel: (model: string) => void;
  updateMetadata: (updates: Partial<DraftModelMetadata>) => void;
  updateDraftName: (name: string) => void;

  // Actions — Chat
  sendStudioChatMessage: (message: string) => Promise<void>;
  handleStudioWsMessage: (msg: any) => void;
  cancelStudioChatLocal: () => void;
  applyStudioActions: (actions: StudioActions) => Promise<void>;
  clearStudioChat: () => void;

  // Actions — Validation & Publish
  validateModel: () => Promise<void>;
  publishModel: () => Promise<void>;
  previewPublish: () => Promise<void>;
  confirmPublish: () => Promise<void>;
  dismissPreview: () => void;

  // Actions — AI
  suggestJoins: () => Promise<void>;
  suggestDisplayNames: () => Promise<void>;
  classifyColumns: () => Promise<void>;

  // Actions — UI
  setActivePreviewTab: (tab: number) => void;
  setStudioHome: (tab: StudioHomeTab | null) => void;
  setError: (error: string | null) => void;

  // Discover review (#1031): only what the review route returns; `failed` when it could not be read.
  /** `hash` is the draft version the items were rendered from; every confirmation names it. */
  discoveryReview: { draftId: string; items: DiscoveryReviewItem[]; hash: string | null; failed: boolean } | null;
  /** The draft object as last loaded or saved: the editor holds unsaved changes whenever it is not this object. */
  savedDraft: DraftModel | null;
  loadDiscoveryReview: (draftId: string) => Promise<void>;
  confirmDiscovery: (ref: string, text: DiscoveryConfirmText) => Promise<ConfirmOutcome>;
  confirmDiscoveryColumn: (column: string, items: Array<{ ref: string; text: DiscoveryConfirmText }>) => Promise<ConfirmOutcome>;
}

/** A confirmation's outcome. A failure's message is one of ours, never the server's text; `ref` names the item. */
export type ConfirmOutcome = { ok: true } | { ok: false; message: string; ref?: string };

export const CONFIRM_FAILED = {
  403: 'Not allowed: confirming needs a signed-in editor of this draft. Nothing was confirmed.',
  409: 'This draft changed since you reviewed it, or this item cannot be confirmed. Nothing was confirmed; the review has been refreshed.',
  400: 'The server did not accept this confirmation as entered. Nothing was confirmed.',
  404: 'An item is no longer in this draft. Refresh the review. Nothing was confirmed.',
  default: 'Confirming failed. Nothing was confirmed.',
  unknown: "We couldn't confirm whether this was saved; the review has been refreshed.",
  unsaved: 'Save your changes before confirming.',
  unavailable: 'The code values of this item could not be read live, so it cannot be confirmed. Refresh the review.',
} as const;

/** Whether the editor holds changes the server has not saved: a confirmation would settle the saved definition. */
export function hasUnsavedChanges(s: Pick<StudioState, 'isDirty' | 'draftModel' | 'savedDraft'>): boolean {
  return s.isDirty || s.draftModel !== s.savedDraft;
}

/** Only a definitive 4xx means nothing was confirmed; anything else (no response, a timeout, a 5xx) is unknown. */
function confirmFailure(err: any, refs: string[]): { outcome: ConfirmOutcome; unknown: boolean } {
  const status = err?.response?.status;
  if (typeof status !== 'number' || status < 400 || status >= 500) {
    return { outcome: { ok: false, message: CONFIRM_FAILED.unknown }, unknown: true };
  }
  const message = CONFIRM_FAILED[status as 400 | 403 | 404 | 409] ?? CONFIRM_FAILED.default;
  // The server names a refused item by its ref; we only match it against refs we sent, and never show its text.
  const said = String(err?.response?.data?.error ?? '');
  const ref = refs.find((r) => said.includes(r));
  return { outcome: ref ? { ok: false, message, ref } : { ok: false, message }, unknown: false };
}

/** The codes a person saw for an item, per code column, exactly as the review on screen returned them (#1194);
 * null when the item is not in that review or any of its code values was unavailable. */
export function codesOf(item: DiscoveryReviewItem | undefined): DiscoveryCodes | null {
  if (!item || item.code_literals === null) return null;
  const out: DiscoveryCodes = {};
  for (const c of item.code_literals) {
    if (!c.values) return null;
    out[c.column] = c.values.map((v) => ({ code: v.code, value: { type: v.value.type, text: v.value.text } }));
  }
  return out;
}

// Every review request is numbered; only the latest request's response is applied.
let reviewRequest = 0;

/** A Discover draft (the server's `is_discover_draft`): marked by its metadata, or any item carrying a label. */
export function isDiscoverDraft(draft: DraftModel | null | undefined): boolean {
  if (!draft) return false;
  if ((draft.metadata as any)?.source === 'discovery') return true;
  return [draft.metrics, draft.dimensions, draft.relationships]
    .some((xs) => (xs as any[] | undefined)?.some((x) => x && typeof x.discovery === 'object' && x.discovery !== null));
}

export type StudioHomeTab = 'perspectives' | 'definitions' | 'new-source' | SemanticLayerTab;
export const STUDIO_HOME_TABS: readonly StudioHomeTab[] = ['perspectives', 'definitions', 'new-source'];
/** The Semantic layer's own tabs (#1510). */
export type SemanticLayerTab = 'overview' | 'changes' | 'health';
export const SEMANTIC_LAYER_TABS: readonly SemanticLayerTab[] = ['overview', 'changes', 'health'];

// ============================================================
// Helper — mutate draft immutably
// ============================================================

function mutateDraft(
  get: () => StudioState,
  set: (partial: Partial<StudioState>) => void,
  mutator: (draft: DraftModel) => DraftModel
) {
  const { draftModel } = get();
  if (!draftModel) return;
  const updated = mutator({ ...draftModel });
  set({ draftModel: updated, isDirty: true, validationResult: null });
}

/** One confirmation request: refused while the editor holds unsaved changes; the review is read again after it,
 * after a 409 (the draft changed since the review) and after any failure whose outcome is unknown, so what is
 * shown is the server's. It names the draft version of the review on screen: the server refuses any other. */
async function sendConfirmation(
  get: () => StudioState, refs: string[], send: (draftId: string, reviewedHash: string, codes: DiscoveryCodes[]) => Promise<void>,
): Promise<ConfirmOutcome> {
  const state = get();
  const id = state.draftModel?.id;
  const review = state.discoveryReview;
  if (!id || !review || review.draftId !== id || review.failed || !review.hash) {
    return { ok: false, message: CONFIRM_FAILED.default };
  }
  if (hasUnsavedChanges(state)) return { ok: false, message: CONFIRM_FAILED.unsaved };
  // Each item approves exactly the code values its review showed (#1194): one unavailable refuses it here.
  const codes = refs.map((r) => codesOf(review.items.find((i) => i.ref === r)));
  if (!codes.every((c): c is DiscoveryCodes => c !== null)) {
    const ref = refs.length === 1 ? refs[0] : undefined;
    return ref ? { ok: false, message: CONFIRM_FAILED.unavailable, ref } : { ok: false, message: CONFIRM_FAILED.unavailable };
  }
  try {
    await send(id, review.hash, codes);
  } catch (err: any) {
    const { outcome, unknown } = confirmFailure(err, refs);
    if (unknown || err?.response?.status === 409) await get().loadDiscoveryReview(id);
    return outcome;
  }
  await get().loadDiscoveryReview(id);
  return { ok: true };
}

// ============================================================
// Store
// ============================================================

const useStudioStore = create<StudioState>((set, get) => ({
  // Initial state
  draftModel: null,
  baselineModel: null,
  isDirty: false,
  draftsList: [],
  productionConflict: false,
  sourceContext: null,
  studioChatMessages: [],
  studioChatThreadId: null,
  isStudioThinking: false,
  studioChatProgress: [],
  studioAgentPanelOpen: false,
  studioAgentPanelWidth: 420,
  toggleStudioAgentPanel: () => set({ studioAgentPanelOpen: !get().studioAgentPanelOpen }),
  setStudioAgentPanelWidth: (w: number) => set({ studioAgentPanelWidth: Math.max(320, Math.min(720, w)) }),
  publishPreview: null,
  isPreviewLoading: false,
  activePreviewTab: 0,
  studioHome: null,
  validationResult: null,
  isLoading: false,
  error: null,
  discoveryReview: null,
  savedDraft: null,

  // ----------------------------------------------------------
  // Discover review (#1031)
  // ----------------------------------------------------------

  loadDiscoveryReview: async (draftId) => {
    const seq = ++reviewRequest;
    const current = () => seq === reviewRequest && get().draftModel?.id === draftId;
    try {
      const { items, content_hash: hash } = await getDiscoveryReview(draftId);
      const ok = Array.isArray(items) && typeof hash === 'string' && hash !== '';
      if (current()) set({ discoveryReview: ok ? { draftId, items, hash, failed: false } : { draftId, items: [], hash: null, failed: true } });
    } catch {
      if (current()) set({ discoveryReview: { draftId, items: [], hash: null, failed: true } });
    }
  },

  confirmDiscovery: async (ref, text) =>
    sendConfirmation(get, [ref], (id, reviewed, codes) => apiConfirmDiscoveryItem(id, reviewed, ref, text, codes[0])),

  confirmDiscoveryColumn: async (column, items) =>
    sendConfirmation(get, items.map((i) => i.ref), (id, reviewed, codes) =>
      apiConfirmDiscoveryBatch(id, reviewed, column, items.map((i, k) => ({ ...i, codes: codes[k] })))),

  // ----------------------------------------------------------
  // Draft CRUD
  // ----------------------------------------------------------

  createNewDraft: async (name, metadata) => {
    set({ isLoading: true, error: null });
    try {
      const draft = await createStudioDraft({ name, metadata, created_by: getCurrentUser() || '' });
      // New draft has no production baseline — changes tab will show "No baseline"
      set({
        draftModel: draft, savedDraft: draft,
        baselineModel: null,
        productionConflict: false,
        isDirty: false,
        validationResult: null,
        studioChatMessages: [],
        isLoading: false,
        studioHome: null,
      });
    } catch (err: any) {
      set({ isLoading: false, error: err?.message || 'Failed to create draft' });
    }
  },

  loadDraft: async (id, signal) => {
    const namespace = getPersistenceNamespace();
    const loadEpoch = navigationEpoch();
    set({ isLoading: true, error: null });
    try {
      const draft = await getStudioDraft(id);
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
      // Extract transient conflict flag from backend and strip from draft object
      const conflict = !!(draft as any)?._productionConflict;
      if ((draft as any)?._productionConflict !== undefined) {
        delete (draft as any)._productionConflict;
      }
      // Fetch the production model as the stable baseline (draft vs prod)
      const sourceModel = draft?.metadata?.source_model;
      const prodSnapshot = sourceModel ? await getProductionSnapshot(sourceModel) : null;
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
      set({
        draftModel: draft, savedDraft: draft,
        baselineModel: prodSnapshot ? structuredClone(prodSnapshot) : null,
        productionConflict: conflict,
        isDirty: false,
        validationResult: null,
        studioChatMessages: [],
        sourceContext: null,
        isLoading: false,
        studioHome: null,
      });
    } catch (err: any) {
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
      set({ isLoading: false, error: err?.message || 'Failed to load draft' });
    }
  },

  loadDraftFromModel: async (modelName, context, opts) => {
    const loadEpoch = navigationEpoch(), namespace = getPersistenceNamespace();
    set({ isLoading: true, error: null });
    try {
      const draft = await apiLoadDraftFromModel(modelName, getCurrentUser() || undefined);
      // Extract transient conflict flag from backend and strip from draft object
      const conflict = !!(draft as any)?._productionConflict;
      if ((draft as any)?._productionConflict !== undefined) {
        delete (draft as any)._productionConflict;
      }
      // Fetch the production model as the stable baseline (draft vs prod)
      const prodSnapshot = await getProductionSnapshot(modelName);
      if (loadEpoch !== navigationEpoch() || namespace !== getPersistenceNamespace()) return;
      // Preserve chat when swapping drafts as part of the same conversation
      // (Apply routing to inherited entity, breadcrumb navigation). Explicit
      // Load-from-dialog still wipes chat.
      const preserveChat = !!opts?.preserveChat;
      set({
        draftModel: draft, savedDraft: draft,
        baselineModel: prodSnapshot ? structuredClone(prodSnapshot) : null,
        productionConflict: conflict,
        isDirty: false,
        validationResult: null,
        ...(preserveChat ? {} : { studioChatMessages: [], studioChatThreadId: null }),
        sourceContext: context
          ? { ...context, modelName, breadcrumb: [] }
          : { domain: '', perspective: '', modelName, breadcrumb: [] },
        isLoading: false,
        studioHome: null,
      });
    } catch (err: any) {
      if (loadEpoch !== navigationEpoch() || namespace !== getPersistenceNamespace()) return;
      set({ isLoading: false, error: err?.message || 'Failed to load model as draft' });
    }
  },

  saveDraft: async () => {
    const { draftModel } = get();
    if (!draftModel) return;
    set({ isLoading: true, error: null });
    try {
      const updated = await updateStudioDraft(draftModel.id, {
        ...draftModel,
        updated_by: getCurrentUser() || '',
      });
      // Don't reset baselineModel — baseline is always the production model, not the last save
      set({ draftModel: updated, savedDraft: updated, isDirty: false, isLoading: false });
    } catch (err: any) {
      set({ isLoading: false, error: err?.message || 'Failed to save draft' });
    }
  },

  fetchDraftsList: async () => {
    // The list is the workspace's (#566): a response that lands after the
    // workspace changed is dropped, and a failure leaves nothing behind that
    // could be read as this workspace's drafts.
    const namespace = getPersistenceNamespace();
    try {
      const drafts = await getStudioDrafts();
      if (namespace !== getPersistenceNamespace()) return false;
      set({ draftsList: drafts });
      return true;
    } catch (err: any) {
      console.error('Failed to fetch drafts list:', err?.message);
      if (namespace === getPersistenceNamespace()) set({ draftsList: [] });
      return false;
    }
  },

  deleteDraft: async (id) => {
    try {
      await deleteStudioDraft(id);
      const { draftModel, draftsList } = get();
      set({
        draftsList: draftsList.filter((d) => d.id !== id),
        ...(draftModel?.id === id ? { draftModel: null, isDirty: false } : {}),
      });
      return true;
    } catch (err: any) {
      set({ error: err?.message || 'Failed to delete draft' });
      return false;
    }
  },

  navigateToInheritedEntity: async (targetModel) => {
    const { draftModel, isDirty, sourceContext } = get();
    if (!draftModel || !targetModel || targetModel === (sourceContext?.modelName)) return;

    // Auto-save current draft so the jump doesn't drop edits (same idiom as previewPublish).
    if (isDirty) {
      try {
        await updateStudioDraft(draftModel.id, {
          ...draftModel,
          updated_by: getCurrentUser() || '',
        });
        set({ isDirty: false, savedDraft: draftModel });
      } catch (err: any) {
        set({ error: err?.response?.data?.error || err?.message || 'Failed to save current draft' });
        return;
      }
    }

    const currentCrumb = {
      modelName: sourceContext?.modelName || draftModel.metadata?.source_model || draftModel.name,
      displayName: draftModel.metadata?.display_name || draftModel.name,
    };
    const priorCrumbs = sourceContext?.breadcrumb || [];
    const nextBreadcrumb = [...priorCrumbs, currentCrumb];

    await get().loadDraftFromModel(targetModel, undefined, { preserveChat: true });
    // loadDraftFromModel resets breadcrumb to []; restore the extended stack now.
    set((s) => ({
      sourceContext: s.sourceContext
        ? { ...s.sourceContext, breadcrumb: nextBreadcrumb }
        : { domain: '', perspective: '', modelName: targetModel, breadcrumb: nextBreadcrumb },
    }));
  },

  popBreadcrumbTo: async (index) => {
    const { draftModel, isDirty, sourceContext } = get();
    if (!sourceContext || !sourceContext.breadcrumb || sourceContext.breadcrumb.length === 0) return;
    const target = sourceContext.breadcrumb[index];
    if (!target) return;

    if (draftModel && isDirty) {
      try {
        await updateStudioDraft(draftModel.id, {
          ...draftModel,
          updated_by: getCurrentUser() || '',
        });
        set({ isDirty: false, savedDraft: draftModel });
      } catch (err: any) {
        set({ error: err?.response?.data?.error || err?.message || 'Failed to save current draft' });
        return;
      }
    }

    const trimmedBreadcrumb = sourceContext.breadcrumb.slice(0, index);
    await get().loadDraftFromModel(target.modelName, undefined, { preserveChat: true });
    set((s) => ({
      sourceContext: s.sourceContext
        ? { ...s.sourceContext, breadcrumb: trimmedBreadcrumb }
        : null,
    }));
  },

  // ----------------------------------------------------------
  // Model mutations
  // ----------------------------------------------------------

  addTable: (table) => mutateDraft(get, set, (d) => ({
    ...d,
    tables: [...d.tables, table],
  })),

  updateTable: (name, updates) => mutateDraft(get, set, (d) => ({
    ...d,
    tables: d.tables.map((t) => (t.name === name ? { ...t, ...updates } : t)),
  })),

  removeTable: (name) => mutateDraft(get, set, (d) => ({
    ...d,
    tables: d.tables.filter((t) => t.name !== name),
  })),

  addColumn: (tableName, column) => mutateDraft(get, set, (d) => ({
    ...d,
    tables: d.tables.map((t) =>
      t.name === tableName ? { ...t, columns: [...t.columns, column] } : t
    ),
  })),

  updateColumn: (tableName, columnName, updates) => mutateDraft(get, set, (d) => ({
    ...d,
    tables: d.tables.map((t) =>
      t.name === tableName
        ? { ...t, columns: t.columns.map((c) => (c.name === columnName ? { ...c, ...updates } : c)) }
        : t
    ),
  })),

  updateColumnByIndex: (tableName, index, updates) => mutateDraft(get, set, (d) => ({
    ...d,
    tables: d.tables.map((t) =>
      t.name === tableName
        ? { ...t, columns: t.columns.map((c, i) => (i === index ? { ...c, ...updates } : c)) }
        : t
    ),
  })),

  removeColumn: (tableName, columnName) => mutateDraft(get, set, (d) => ({
    ...d,
    tables: d.tables.map((t) =>
      t.name === tableName
        ? { ...t, columns: t.columns.filter((c) => c.name !== columnName) }
        : t
    ),
  })),

  removeColumnByIndex: (tableName, index) => mutateDraft(get, set, (d) => ({
    ...d,
    tables: d.tables.map((t) =>
      t.name === tableName
        ? { ...t, columns: t.columns.filter((_, i) => i !== index) }
        : t
    ),
  })),

  addRelationship: (rel) => mutateDraft(get, set, (d) => ({
    ...d,
    relationships: [...d.relationships, rel],
  })),

  updateRelationship: (name, updates) => mutateDraft(get, set, (d) => ({
    ...d,
    relationships: d.relationships.map((r) => (r.name === name ? { ...r, ...updates } : r)),
  })),

  removeRelationship: (name) => mutateDraft(get, set, (d) => ({
    ...d,
    relationships: d.relationships.filter((r) => r.name !== name),
  })),

  addMetric: (metric) => mutateDraft(get, set, (d) => ({
    ...d,
    metrics: [...d.metrics, metric],
  })),

  updateMetric: (name, updates) => mutateDraft(get, set, (d) => ({
    ...d,
    metrics: d.metrics.map((m) => (m.name === name ? { ...m, ...updates } : m)),
  })),

  removeMetric: (name) => mutateDraft(get, set, (d) => ({
    ...d,
    metrics: d.metrics.filter((m) => m.name !== name),
  })),

  addDimension: (dim) => mutateDraft(get, set, (d) => ({
    ...d,
    dimensions: [...d.dimensions, dim],
  })),

  updateDimension: (name, updates) => mutateDraft(get, set, (d) => ({
    ...d,
    dimensions: d.dimensions.map((dim) => (dim.name === name ? { ...dim, ...updates } : dim)),
  })),

  removeDimension: (name) => mutateDraft(get, set, (d) => ({
    ...d,
    dimensions: d.dimensions.filter((dim) => dim.name !== name),
  })),

  addEntity: (entity) => mutateDraft(get, set, (d) => ({
    ...d,
    entities: [...d.entities, entity],
  })),

  updateEntity: (name, updates) => mutateDraft(get, set, (d) => ({
    ...d,
    entities: d.entities.map((e) => (e.name === name ? { ...e, ...updates } : e)),
  })),

  removeEntity: (name) => mutateDraft(get, set, (d) => ({
    ...d,
    entities: d.entities.filter((e) => e.name !== name),
  })),

  addMetricGroup: (group) => mutateDraft(get, set, (d) => ({
    ...d,
    metricGroups: [...d.metricGroups, group],
  })),

  updateMetricGroup: (name, updates) => mutateDraft(get, set, (d) => ({
    ...d,
    metricGroups: d.metricGroups.map((g) => (g.name === name ? { ...g, ...updates } : g)),
  })),

  removeMetricGroup: (name) => mutateDraft(get, set, (d) => ({
    ...d,
    metricGroups: d.metricGroups.filter((g) => g.name !== name),
  })),

  setMetricGroups: (groups) => mutateDraft(get, set, (d) => ({
    ...d,
    metricGroups: groups,
  })),

  addAggregateSource: (source) => mutateDraft(get, set, (d) => ({
    ...d,
    aggregateSources: [...(d.aggregateSources || []), source],
  })),

  updateAggregateSource: (table, updates) => mutateDraft(get, set, (d) => ({
    ...d,
    aggregateSources: (d.aggregateSources || []).map((s) =>
      s.table === table ? { ...s, ...updates } : s
    ),
  })),

  removeAggregateSource: (table) => mutateDraft(get, set, (d) => ({
    ...d,
    aggregateSources: (d.aggregateSources || []).filter((s) => s.table !== table),
  })),

  setModelType: (type) => mutateDraft(get, set, (d) => ({
    ...d,
    model_type: type,
  })),

  setCoreModel: (model) => mutateDraft(get, set, (d) => ({
    ...d,
    core_model: model,
  })),

  updateMetadata: (updates) => mutateDraft(get, set, (d) => ({
    ...d,
    metadata: { ...d.metadata, ...updates },
  })),

  updateDraftName: (name) => mutateDraft(get, set, (d) => ({
    ...d,
    name,
  })),

  // ----------------------------------------------------------
  // Chat
  // ----------------------------------------------------------

  sendStudioChatMessage: async (message) => {
    const { draftModel, studioChatMessages, studioChatThreadId } = get();

    // Lazy-init a thread id so every turn in the same chat is linked in traces.
    // Cleared by clearStudioChat() so a fresh chat starts a fresh thread.
    const threadId = studioChatThreadId || `sat_${generateId()}`;

    const userMsg: StudioChatMessage = {
      id: generateId(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    set({
      studioChatMessages: [...studioChatMessages, userMsg],
      studioChatThreadId: threadId,
      isStudioThinking: true,
      studioChatProgress: [],
    });

    const history = studioChatMessages.map((m) => ({
      role: m.role as 'user' | 'assistant',
      content: m.content,
    }));

    try {
      const data = await studioAgentChat({
        message,
        conversationHistory: history,
        draftModel,
        thread_id: threadId,
      });
      const assistantMsg: StudioChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: data.reply || '',
        timestamp: new Date().toISOString(),
        thinking: data.thinking,
        actions: data.actions,
        suggestedFollowUps: data.suggestedFollowUps,
      };
      set({
        studioChatMessages: [...get().studioChatMessages, assistantMsg],
        isStudioThinking: false,
        studioChatProgress: [],
      });
    } catch (err: any) {
      const backendError =
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        'Request failed';
      const errorMsg: StudioChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: `Error: ${backendError}`,
        timestamp: new Date().toISOString(),
      };
      set({
        studioChatMessages: [...get().studioChatMessages, errorMsg],
        isStudioThinking: false,
        studioChatProgress: [],
      });
    }
  },

  handleStudioWsMessage: (msg) => {
    if (msg.type === 'progress') {
      const stepText = msg.text as string;
      set({
        studioChatProgress: [
          ...get().studioChatProgress.map((s) => ({ ...s, done: true })),
          { id: generateId(), text: stepText, done: false },
        ],
      });
    } else if (msg.type === 'error') {
      const errText = msg.error as string;
      const errorMsg: StudioChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: `Sorry, I encountered an error: ${errText || 'Unknown error'}`,
        timestamp: new Date().toISOString(),
      };
      set({
        studioChatMessages: [...get().studioChatMessages, errorMsg],
        isStudioThinking: false,
        studioChatProgress: [],
      });
    } else if (msg.type === 'done') {
      const data = msg.data || {};
      const assistantMsg: StudioChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: data.reply || '',
        timestamp: new Date().toISOString(),
        thinking: data.thinking,
        actions: data.actions,
        suggestedFollowUps: data.suggestedFollowUps,
      };
      set({
        studioChatMessages: [...get().studioChatMessages, assistantMsg],
        isStudioThinking: false,
        studioChatProgress: [],
      });
    }
  },

  cancelStudioChatLocal: () => {
    set({
      isStudioThinking: false,
      studioChatProgress: [],
    });
  },

  applyStudioActions: async (actions) => {
    const { draftModel, sourceContext } = get();
    if (!draftModel) return;

    // If the agent targeted a different model, swap into that draft first
    // (auto-save current + push breadcrumb). Only do this when the target
    // truly differs from what's currently open.
    const currentModelName = sourceContext?.modelName
      || draftModel.metadata?.source_model
      || draftModel.name;
    if (actions.targetModel && actions.targetModel !== currentModelName) {
      await get().navigateToInheritedEntity(actions.targetModel);
      // After the swap, re-run with targetModel stripped so we don't recurse.
      const { targetModel, ...rest } = actions;
      return get().applyStudioActions(rest);
    }

    const current = get().draftModel;
    if (!current) return;
    let updated = { ...current };

    if (actions.addTables) {
      updated.tables = [...updated.tables, ...actions.addTables];
    }
    if (actions.addColumnsToTable) {
      for (const entry of actions.addColumnsToTable) {
        updated.tables = updated.tables.map((t) =>
          t.name === entry.table
            ? { ...t, columns: [...t.columns, ...entry.columns] }
            : t
        );
      }
    }
    if (actions.removeTables) {
      updated.tables = updated.tables.filter((t) => !actions.removeTables!.includes(t.name));
    }
    if (actions.addMetrics) {
      updated.metrics = [...updated.metrics, ...actions.addMetrics];
    }
    if (actions.removeMetrics) {
      updated.metrics = updated.metrics.filter((m) => !actions.removeMetrics!.includes(m.name));
    }
    if (actions.addDimensions) {
      updated.dimensions = [...updated.dimensions, ...actions.addDimensions];
    }
    if (actions.removeDimensions) {
      updated.dimensions = updated.dimensions.filter((d) => !actions.removeDimensions!.includes(d.name));
    }
    if (actions.addRelationships) {
      updated.relationships = [...updated.relationships, ...actions.addRelationships];
    }
    if (actions.suggestJoins) {
      updated.relationships = [...updated.relationships, ...actions.suggestJoins];
    }
    if (actions.addEntities) {
      updated.entities = [...updated.entities, ...actions.addEntities];
    }
    if (actions.addMetricGroups) {
      updated.metricGroups = [...updated.metricGroups, ...actions.addMetricGroups];
    }
    if (actions.updateMetadata) {
      updated.metadata = { ...updated.metadata, ...actions.updateMetadata };
    }
    if (actions.addAggregateSources) {
      updated.aggregateSources = [...(updated.aggregateSources || []), ...actions.addAggregateSources];
    }
    if (actions.removeAggregateSources) {
      updated.aggregateSources = (updated.aggregateSources || []).filter(
        (s) => !actions.removeAggregateSources!.includes(s.table)
      );
    }

    set({ draftModel: updated, isDirty: true, validationResult: null });
  },

  clearStudioChat: () => {
    set({ studioChatMessages: [], studioChatThreadId: null });
  },

  // ----------------------------------------------------------
  // Validation & Publish
  // ----------------------------------------------------------

  validateModel: async () => {
    const { draftModel } = get();
    if (!draftModel) return;
    set({ isLoading: true, error: null });
    try {
      const result = await validateStudioModel(draftModel);
      set({ validationResult: result, isLoading: false });
    } catch (err: any) {
      set({ isLoading: false, error: err?.message || 'Validation failed' });
    }
  },

  publishModel: async () => {
    // Delegate to preview flow — show preview dialog first
    await get().previewPublish();
  },

  previewPublish: async () => {
    const { draftModel, isDirty } = get();
    if (!draftModel) return;
    set({ isPreviewLoading: true, error: null });
    try {
      // Auto-save unsaved changes so the preview reflects the latest edits
      if (isDirty) {
        await updateStudioDraft(draftModel.id, {
          ...draftModel,
          updated_by: getCurrentUser() || '',
        });
        set({ isDirty: false, savedDraft: draftModel });
      }
      const preview = await previewStudioPublish(draftModel.id);
      // The dialog counts the Discover items that will not publish, so the review is read fresh first.
      if (isDiscoverDraft(draftModel)) await get().loadDiscoveryReview(draftModel.id);
      set({ publishPreview: preview, isPreviewLoading: false });
    } catch (err: any) {
      set({ isPreviewLoading: false, error: err?.response?.data?.error || err?.message || 'Preview failed' });
    }
  },

  confirmPublish: async () => {
    const { draftModel, isDirty } = get();
    if (!draftModel) return;
    set({ isLoading: true, error: null });
    try {
      // Auto-save unsaved changes so the publish uses the latest edits
      if (isDirty) {
        await updateStudioDraft(draftModel.id, {
          ...draftModel,
          updated_by: getCurrentUser() || '',
        });
      }
      const result = await publishStudioModel(draftModel.id);
      // After publish, the draft IS the new production — update baseline, clear conflict
      set({
        draftModel: result.draft, savedDraft: result.draft,
        baselineModel: structuredClone(result.draft),
        productionConflict: false,
        isDirty: false,
        isLoading: false,
        publishPreview: null,
      });
    } catch (err: any) {
      set({ isLoading: false, error: err?.response?.data?.error || err?.message || 'Publish failed' });
    }
  },

  dismissPreview: () => {
    set({ publishPreview: null });
  },

  // ----------------------------------------------------------
  // AI Suggestions
  // ----------------------------------------------------------

  suggestJoins: async () => {
    const { draftModel } = get();
    if (!draftModel || draftModel.tables.length < 2) return;
    set({ isLoading: true });
    try {
      const suggestions = await studioSuggestJoins(draftModel.tables);
      // Filter out already-existing relationships
      const existingNames = new Set(draftModel.relationships.map((r) => r.name));
      const newSuggestions = (suggestions || []).filter((s: any) => !existingNames.has(s.name));
      if (newSuggestions.length > 0) {
        set({
          draftModel: {
            ...draftModel,
            relationships: [...draftModel.relationships, ...newSuggestions],
          },
          isDirty: true,
          validationResult: null,
        });
      }
      set({ isLoading: false });
    } catch (err: any) {
      set({ isLoading: false, error: err?.message || 'Failed to suggest joins' });
    }
  },

  suggestDisplayNames: async () => {
    const { draftModel } = get();
    if (!draftModel) return;
    const allCols: string[] = [];
    for (const table of draftModel.tables) {
      for (const col of table.columns) {
        allCols.push(col.name);
      }
    }
    if (allCols.length === 0) return;
    try {
      await studioSuggestDisplayNames(allCols);
      // Display names are primarily used in metric/dimension definitions, not stored on columns
    } catch (err: any) {
      console.error('Failed to suggest display names:', err?.message);
    }
  },

  classifyColumns: async () => {
    const { draftModel } = get();
    if (!draftModel) return;
    const allCols: { name: string; type: string }[] = [];
    for (const table of draftModel.tables) {
      for (const col of table.columns) {
        allCols.push({ name: col.name, type: col.type });
      }
    }
    if (allCols.length === 0) return;
    try {
      await studioClassifyColumns(allCols);
    } catch (err: any) {
      console.error('Failed to classify columns:', err?.message);
    }
  },

  // ----------------------------------------------------------
  // UI
  // ----------------------------------------------------------

  setActivePreviewTab: (tab) => set({ activePreviewTab: tab }),
  setStudioHome: (tab) => set({ studioHome: tab }),
  setError: (error) => set({ error }),
}));

export default useStudioStore;
